import { cn } from '@plato/ui/util/classes';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';
import { Button } from '../button.tsx';
import { useToast } from '../use-toast.tsx';

type CopyButtonProps = {
    className?: string;
    textToCopy: string;
    label?: string;
};

export function CopyButton({ className, textToCopy, label }: CopyButtonProps) {
    const { t } = useTranslation();
    const { mutate: onCopy } = useCopy();

    const labelText = label ?? t('copy_id_tooltip', { ns: 'common' });

    return (
        <Button
            // Compact glyph that flows with text: no resting background, and the
            // padded hit area is folded back vertically so it never stretches the line.
            className={cn('h-auto w-auto min-w-0 p-1.5 -my-1.5 bg-transparent text-neutral-600', className)}
            icon={CopyIcon}
            variant="icon"
            aria-label={labelText}
            onClick={() => {
                onCopy(textToCopy);
            }}
        />
    );
}

type TitleWithCopyButtonProps = {
    text: string;
    textToCopy: string;
    label?: string;
};

/**
 * A title with an inline copy glyph glued to its last word, so the glyph wraps
 * together with that word instead of orphaning onto its own line.
 */
export function TitleWithCopyButton({ text, textToCopy, label }: TitleWithCopyButtonProps) {
    const words = text.trim().split(' ');
    const lastWord = words.pop();

    return (
        <span>
            {words.length > 0 && `${words.join(' ')} `}
            <span className="whitespace-nowrap">
                {lastWord}
                <CopyButton className="ml-cozy align-middle" textToCopy={textToCopy} label={label} />
            </span>
        </span>
    );
}

function useCopy(): UseMutationResult<boolean, unknown, string> {
    const [, copy] = useCopyToClipboard();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: copy,
        onError: () => {
            toast({
                icon: 'warning',
                title: t('copy_failed', { ns: 'toasts' }),
            });
        },
        onSuccess: () => {
            toast({
                icon: 'success',
                title: t('copy_successful', { ns: 'toasts' }),
            });
        },
    });
}
